import { NavLink } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { useEnabledFeatures } from "@/hooks/useEnabledFeatures";
import { LogoMark } from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import NotificationBell from "@/components/NotificationBell";
import PantryIconLink from "@/components/PantryIconLink";
import FriendsIconLink from "@/components/FriendsIconLink";
import SpNotifier from "@/components/SpNotifier";
import PullToRefresh from "@/components/PullToRefresh";
import { hapticSelection } from "@/lib/haptics";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LogOut, LayoutDashboard, Users, CalendarDays, UtensilsCrossed, LineChart, BookOpen, UserCircle, MessageCircle, ChefHat, Rss, Dumbbell, Refrigerator, UserPlus, Utensils, CircleDot, Trophy, ShieldCheck } from "lucide-react";
export default function Layout({ children, role }) {
  const { user, logout } = useAuth();
  const { t } = useT();

  // TikTok/Instagram-style bottom bar: shrinks when scrolling down,
  // grows back as soon as the user scrolls up (or is at the top).
  const [navCompact, setNavCompact] = useState(false);
  const lastY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y <= 24) setNavCompact(false);
      else if (y > lastY.current + 2) setNavCompact(true);
      else if (y < lastY.current - 2) setNavCompact(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const { isEnabled } = useEnabledFeatures();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const nutritionistNav = [
    { to: "/nutrizionista", label: t("nav.dashboard"), icon: LayoutDashboard, exact: true },
    { to: "/nutrizionista/clienti", label: t("nav.clients"), icon: Users },
    { to: "/nutrizionista/ricette", label: t("nav.recipes"), icon: ChefHat },
    { to: "/nutrizionista/profilo", label: t("nav.profile"), icon: UserCircle },
  ];
  // Client nav — items with a `feature` key are filtered out when the
  // client disables that feature in Profile → "Le mie funzioni". Core
  // navigation (dashboard, diary, chat, profile) is never gated.
  const rawClientNav = [
    { to: "/app", label: t("nav.dashboard"), icon: LayoutDashboard, exact: true },
    { to: "/app/menu-settimanale", label: t("nav.weeklyMenu"), icon: CalendarDays },
    { to: "/app/diario", label: t("nav.diary"), icon: BookOpen },
    { to: "/app/ricette", label: t("nav.recipes"), icon: ChefHat },
    { to: "/app/piatto-solano", label: t("nav.plate"), icon: CircleDot, feature: "plate_solano" },
    { to: "/app/allenamenti", label: t("nav.workouts"), icon: Dumbbell, feature: "workouts" },
    { to: "/app/progressi", label: t("nav.progress"), icon: LineChart },
    { to: "/app/premi", label: t("rewards.navLabel"), icon: Trophy, feature: "gamification" },
    { to: "/app/ristorante", label: t("nav.restaurant"), icon: Utensils },
    { to: "/app/dispensa", label: t("nav.pantry"), icon: Refrigerator, feature: "pantry" },
    { to: "/app/amici", label: t("nav.friends"), icon: UserPlus },
    { to: "/app/profilo", label: t("nav.profile"), icon: UserCircle },
  ];
  const clientNav = rawClientNav.filter((it) => !it.feature || isEnabled(it.feature));
  // Super-admin: ONE admin area (the coach dashboard is merged into /admin).
  const adminNav = [
    { to: "/admin", label: t("admin.navAdmin"), icon: ShieldCheck, exact: true },
    ...nutritionistNav.filter((n) => n.to !== "/nutrizionista").map((n) =>
      n.to === "/nutrizionista/clienti" ? { ...n, to: "/admin?tab=users" } : n),
  ];
  const isSuperAdmin = user?.role === "super_admin";
  // "/admin" and "/admin?tab=users" share a pathname → decide active by query.
  const tabActive = (item, isActive) => {
    if (!isActive || !item.to.startsWith("/admin")) return isActive;
    const wantUsers = item.to.includes("tab=users");
    const hasUsers = (window.location.search || "").includes("tab=users");
    return wantUsers ? hasUsers : !hasUsers;
  };
  const [logoutAsk, setLogoutAsk] = useState(false);
  const askLogout = () => setLogoutAsk(true);
  const nav = isSuperAdmin ? adminNav : role === "nutritionist" || role === "coach" ? nutritionistNav : clientNav;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {role === "client" && <SpNotifier />}
      <aside className="w-64 shrink-0 hidden md:flex flex-col border-r border-slate-200 bg-white">
        <div className="px-6 py-6 border-b border-slate-100 flex items-center justify-between">
          <LogoMark size={36} />
          <NotificationBell />
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.exact}
                data-testid={`nav-${item.to}`}
                className={({ isActive }) => `nav-link ${tabActive(item, isActive) ? "active" : ""}`}>
                <Icon style={{ width: 18, height: 18 }} className="shrink-0" />
                <span className="text-sm">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-slate-100 space-y-3">
          <LanguageSwitcher />
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" /> :
              <div className="h-8 w-8 rounded-full bg-[#5A8B8F]/15 text-[#2C5254] flex items-center justify-center text-xs font-semibold">{user?.name?.[0] || "U"}</div>}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={askLogout} className="w-full nav-link text-slate-500 hover:text-red-600">
            <LogOut className="h-4 w-4" /> {t("common.logout")}
          </button>
        </div>
      </aside>

      <div
        className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          height: "calc(72px + env(safe-area-inset-top))",
          transform: "translate3d(0, 0, 0)",
          WebkitTransform: "translate3d(0, 0, 0)",
          willChange: "transform",
        }}
      >
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex items-center gap-1.5 -ml-1 pl-1 pr-2 py-1 rounded-xl hover:bg-slate-50 active:bg-slate-100 active:scale-95 transition"
          data-testid="mobile-logo-menu"
          aria-label={t("nav.openMenu")}
        >
          <LogoMark size={44} />
        </button>
        <div className="flex items-center gap-1.5">
          {/* Primary actions (accent) — LEFT */}
          {role === "client" && (
            <NavLink
              to="/app/menu-settimanale"
              data-testid="mobile-weekly-link"
              className="h-7 w-7 rounded-full bg-[#5A8B8F]/10 text-[#2C5254] flex items-center justify-center hover:bg-[#5A8B8F]/20 transition"
              aria-label={t("nav.weeklyMenu")}
              title={t("nav.weeklyMenu")}
            >
              <UtensilsCrossed className="h-3 w-3" />
            </NavLink>
          )}
          {role === "client" && (
            <NavLink
              to="/app/ristorante"
              data-testid="mobile-restaurant-link"
              className="h-7 w-7 rounded-full bg-gradient-to-br from-[#E85D1C]/15 to-[#E8951C]/15 text-[#E85D1C] flex items-center justify-center hover:from-[#E85D1C]/25 hover:to-[#E8951C]/25 transition"
              aria-label={t("nav.restaurant")}
              title={t("nav.restaurant")}
            >
              <Utensils className="h-3 w-3" />
            </NavLink>
          )}
          {role === "client" && isEnabled("pantry") && <PantryIconLink />}
          {role === "client" && <FriendsIconLink />}
          <LanguageSwitcher variant="icon" />

          {/* Divider */}
          <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" aria-hidden />

          {/* Settings group (subtle) — RIGHT */}
          <NotificationBell />
          {role === "client" && (
            <NavLink
              to="/app/profilo"
              data-testid="mobile-profile-link"
              className="h-8 w-8 rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition"
              aria-label={t("nav.profile")}
              title={t("nav.profile")}
            >
              <UserCircle className="h-4 w-4" />
            </NavLink>
          )}
          {(role === "nutritionist" || role === "super_admin" || role === "coach") && (
            <NavLink
              to="/nutrizionista/profilo"
              data-testid="mobile-profile-link"
              className="h-8 w-8 rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition"
              aria-label={t("nav.profile")}
              title={t("nav.profile")}
            >
              <UserCircle className="h-4 w-4" />
            </NavLink>
          )}
          <button
            onClick={askLogout}
            className="h-8 w-8 rounded-full text-slate-500 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition"
            data-testid="logout-btn-mobile"
            aria-label={t("common.logout")}
            title={t("common.logout")}
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      <main className="flex-1 min-w-0 md:pt-0 pt-[calc(72px+env(safe-area-inset-top))] md:pb-0 pb-28"><PullToRefresh />{children}</main>

      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 px-4 pb-[max(env(safe-area-inset-bottom),0.9rem)] pt-2 pointer-events-none"
        style={{
          // Force GPU compositing so iOS Safari keeps the bar pinned to the
          // visual viewport when the address bar auto-hides during scroll.
          transform: "translate3d(0, 0, 0)",
          WebkitTransform: "translate3d(0, 0, 0)",
          willChange: "transform",
          WebkitBackfaceVisibility: "hidden",
          backfaceVisibility: "hidden",
        }}
      >
        <div
          className={`pointer-events-auto flex justify-around items-center rounded-full bg-white/95 dark:bg-[#1a1d1e]/95 backdrop-blur-xl border border-slate-200/80 dark:border-slate-700 shadow-[0_12px_32px_rgba(0,0,0,0.16)] px-2.5 gap-0.5 transition-all duration-300 ease-out ${
            navCompact && role === "client" ? "py-1 mx-6 scale-[0.94]" : "py-2 mx-0 scale-100"
          }`}
          style={{ transformOrigin: "50% 100%" }}
        >
          {(role === "client" ? nav.filter(n => n.to !== "/app/menu-settimanale" && n.to !== "/app/piatto-solano") : nav).slice(0, 5).map((item) => {
            const Icon = item.icon;
            const shortLabel = item.label.split(" ")[0];
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={() => hapticSelection()}
                data-testid={`bottomnav-${item.to.split("/").pop() || "root"}`}
                className={({ isActive }) => `group relative flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 rounded-full px-1 py-1.5 text-[10px] leading-tight transition-all duration-200 active:scale-90 ${
                  tabActive(item, isActive)
                    ? "solano-nav-active font-semibold"
                    : "text-slate-500 dark:text-slate-400 hover:text-[#2C5254]"
                }`}
              >
                {({ isActive: raw }) => { const isActive = tabActive(item, raw); return (
                  <>
                    <span className={`transition-transform duration-300 ${isActive ? "scale-110" : "scale-100 group-active:scale-90"}`}>
                      <Icon size={20} />
                    </span>
                    <span className={`truncate w-full text-center transition-all duration-300 overflow-hidden ${navCompact && role === "client" ? "max-h-0 opacity-0" : "max-h-4 opacity-100"}`}>{shortLabel}</span>
                    {isActive && (
                      <span
                        className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full"
                        style={{ background: "var(--solano-accent, #5A8B8F)" }}
                        aria-hidden
                      />
                    )}
                  </>
                ); }}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Full navigation drawer (opens on logo tap, mobile) */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col bg-white dark:bg-[#111315]" data-testid="mobile-nav-drawer">
          <div className="px-5 py-5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
            <LogoMark size={30} />
            <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-2">
            {nav.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 mb-0.5 text-sm transition ${
                    isActive
                      ? "bg-[#EAF3F4] dark:bg-[#22383a] text-[#2C5254] dark:text-teal-200 font-semibold"
                      : "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                  }`}
                  data-testid={`drawer-link-${item.to.split("/").pop() || "root"}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>
          <div className="border-t border-slate-100 dark:border-slate-800 px-2 py-2">
            <button
              type="button"
              onClick={() => { setDrawerOpen(false); askLogout(); }}
              className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
              data-testid="drawer-logout-btn"
            >
              <LogOut className="h-4 w-4" />
              <span>{t("common.logout")}</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={logoutAsk} onOpenChange={setLogoutAsk}>
        <DialogContent className="max-w-xs rounded-3xl" data-testid="logout-confirm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{t("admin.logoutConfirmTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">{t("admin.logoutConfirmBody")}</p>
          <div className="flex gap-2 mt-1">
            <button type="button" onClick={() => setLogoutAsk(false)} data-testid="logout-cancel"
              className="flex-1 h-11 rounded-full border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-700 dark:text-slate-200 active:scale-95 transition">
              {t("common.cancel")}
            </button>
            <button type="button" onClick={() => { setLogoutAsk(false); logout(); }} data-testid="logout-confirm-btn"
              className="flex-1 h-11 rounded-full bg-red-500 text-white text-sm font-bold active:scale-95 transition">
              {t("common.logout")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function PageHeader({ title, subtitle, right, mascot }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-8">
      <div className="flex items-start gap-4 min-w-0">
        {mascot}
        <div className="min-w-0">
          <h1 className="font-heading text-3xl md:text-4xl text-[#1A1D1E] font-semibold break-words hyphens-auto">{title}</h1>
          {subtitle && <p className="mt-2 text-slate-600">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}
